import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Flex from 'components/common/Flex';
import IconItem from 'components/common/icon/IconItem';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Form } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const StatsItem = ({ stat }) => {
    const {
        icon,
        color,
        title,
        amount,
        amountComp,
        caret,
        caretColor,
        target,
        percentage
    } = stat;
    return (
        <>
            <Flex
                justifyContent="center"
                alignItems="center"
                className="mb-3 position-static"
            >
                <IconItem
                    tag="div"
                    icon={icon}
                    bg={`soft-${color}`}
                    color={color}
                    size="sm"
                    iconClass="fs--1"
                    className="me-2 shadow-none"
                />
                <h6 className="mb-0 flex-1">{title}</h6>
            </Flex>
            <Flex
                justifyContent="center"
                alignItems="center">
                <p className={`font-sans-serif lh-1 mb-1 fs-4 pe-2 text-${caretColor}`}> {amount}</p>
                <div className="d-flex flex-column">
                    <FontAwesomeIcon
                        icon={caret}
                        className={`me-1 mb-0 fs-1 text-${caretColor}`}
                    />
                    <p className={`font-sans-serif lh-1 mb-1 fs--1 text-${caretColor}`}>{percentage}%</p>
                </div>
            </Flex>
            <Flex justifyContent="center">
                <b className="fs--1 mb-0 mt-0 text-center text-nowrap">{target}</b>
            </Flex>
            {amountComp && (
                <Flex className="mt-2" justifyContent="center">
                    <p className="fs--1 mb-0 text-muted">
                        {amountComp}
                    </p>
                </Flex>
            )}
        </>
    );
};

const MarketingStats = () => {
    const [stats, setStats] = useState([]);
    const [periodComparison, setPeriodComparison] = useState('lastYear');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [cargando, setCargando] = useState(true);
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
        years.push(year);
    }

    const comparisonOptions = useMemo(() => [
        { value: 'lastMonth', label: 'mes actual vs. Mes Pasado' },
        { value: 'lastYearMonth', label: 'mes actual vs. Mismo Mes Año Pasado' },
        { value: 'lastYear', label: 'año actual vs. Año Pasado' }
    ], []);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatCurrencyWithDecimals = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const calculatePercentageChange = (current, previous) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return ((current - previous) / previous) * 100;
    };

    const ObtenerEstadisticasMarketing = async (anio, comparison) => {
        setCargando(true);
        try {
            const response = await fetch(`/api/marketing/ObtenerEstadisticasMarketing?anio=${anio}&comparison=${comparison}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const dataset = JSON.parse(data.detalles);
                    const rows = dataset.table2 || [];
                    
                    const mainData = rows.find(r => r.period === 'main') || {};
                    const compData = rows.find(r => r.period === 'comp') || {};

                    const leadsChange = calculatePercentageChange(mainData.iTotalLeads || 0, compData.iTotalLeads || 0);
                    const conversionChange = calculatePercentageChange(mainData.iConversiones || 0, compData.iConversiones || 0);
                    const inversionChange = calculatePercentageChange(mainData.dInversionMXN || 0, compData.dInversionMXN || 0);
                    const ventaChange = calculatePercentageChange(mainData.dVentaNuevaMXN || 0, compData.dVentaNuevaMXN || 0);
                    const costoPorResultadoChange = calculatePercentageChange(mainData.dCostoPorResultado || 0, compData.dCostoPorResultado || 0);

                    // Calculate ROI percentage (return / investment * 100)
                    const roi = mainData.dInversionMXN > 0 
                        ? (mainData.dVentaNuevaMXN / mainData.dInversionMXN) * 100 
                        : 0;
                    const roiComp = compData.dInversionMXN > 0 
                        ? (compData.dVentaNuevaMXN / compData.dInversionMXN) * 100 
                        : 0;
                    const roiChange = calculatePercentageChange(roi, roiComp);

                    const targetText = `${mainData.vPeriodLabel || 'Periodo actual'} vs. ${compData.vPeriodLabel || 'Periodo anterior'}`;

                    const statsData = [
                        {
                            id: 1,
                            icon: 'users',
                            color: 'primary',
                            title: 'Leads',
                            amount: (mainData.iTotalLeads || 0).toString(),
                            amountComp: (compData.iTotalLeads || 0).toString(),
                            caret: leadsChange >= 0 ? 'caret-up' : 'caret-down',
                            caretColor: leadsChange >= 0 ? 'success' : 'danger',
                            target: targetText,
                            percentage: Math.abs(leadsChange).toFixed(1)
                        },
                        {
                            id: 2,
                            icon: 'check-circle',
                            color: 'success',
                            title: 'Conversions',
                            amount: (mainData.iConversiones || 0).toString(),
                            amountComp: (compData.iConversiones || 0).toString(),
                            caret: conversionChange >= 0 ? 'caret-up' : 'caret-down',
                            caretColor: conversionChange >= 0 ? 'success' : 'danger',
                            target: targetText,
                            percentage: Math.abs(conversionChange).toFixed(1)
                        },
                        {
                            id: 3,
                            icon: 'dollar-sign',
                            color: 'info',
                            title: 'Investment',
                            amount: formatCurrency(mainData.dInversionMXN || 0),
                            amountComp: formatCurrency(compData.dInversionMXN || 0),
                            caret: inversionChange >= 0 ? 'caret-up' : 'caret-down',
                            caretColor: inversionChange >= 0 ? 'danger' : 'success',
                            target: targetText,
                            percentage: Math.abs(inversionChange).toFixed(1)
                        },
                        {
                            id: 4,
                            icon: 'chart-line',
                            color: 'warning',
                            title: 'ROI',
                            amount: roi > 0 ? `${roi.toFixed(0)}%` : 'N/A',
                            amountComp: roiComp > 0 ? `${roiComp.toFixed(0)}%` : 'N/A',
                            caret: roiChange >= 0 ? 'caret-up' : 'caret-down',
                            caretColor: roiChange >= 0 ? 'success' : 'danger',
                            target: targetText,
                            percentage: Math.abs(roiChange).toFixed(1)
                        },
                        {
                            id: 5,
                            icon: 'bullseye',
                            color: 'danger',
                            title: 'CPL',
                            amount: formatCurrencyWithDecimals(mainData.dCostoPorResultado || 0),
                            amountComp: formatCurrencyWithDecimals(compData.dCostoPorResultado || 0),
                            caret: costoPorResultadoChange >= 0 ? 'caret-up' : 'caret-down',
                            caretColor: costoPorResultadoChange >= 0 ? 'danger' : 'success',
                            target: targetText,
                            percentage: Math.abs(costoPorResultadoChange).toFixed(1)
                        },
                        {
                            id: 6,
                            icon: 'shopping-cart',
                            color: 'secondary',
                            title: 'New Sales',
                            amount: formatCurrency(mainData.dVentaNuevaMXN || 0),
                            amountComp: formatCurrency(compData.dVentaNuevaMXN || 0),
                            caret: ventaChange >= 0 ? 'caret-up' : 'caret-down',
                            caretColor: ventaChange >= 0 ? 'success' : 'danger',
                            target: targetText,
                            percentage: Math.abs(ventaChange).toFixed(1)
                        }
                    ];

                    setStats(statsData);
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener estadísticas');
                    setStats([]);
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
                setStats([]);
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición: ' + error.message);
            console.error('Error fetching marketing stats:', error);
            setStats([]);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        if (token) {
            ObtenerEstadisticasMarketing(selectedYear, periodComparison);
        }
    }, [selectedYear, periodComparison, token]);

    return (
        <Card>
            <Card.Body>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 8 }}>
                    <Form.Select
                        size="sm"
                        value={selectedYear}
                        style={{ width: 120 }}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </Form.Select>
                    <Form.Select
                        size="sm"
                        value={periodComparison}
                        style={{ width: 350 }}
                        onChange={(e) => setPeriodComparison(e.target.value)}
                    >
                        {comparisonOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Select>
                </div>
                <Row>
                    {cargando ? (
                        <Skeleton height={50} count={6} />
                    ) : stats.length === 0 ? (
                        <Col xs={12} className="text-center py-3">
                            <p className="text-muted">No hay datos disponibles</p>
                        </Col>
                    ) : (
                        stats.map((stat, index) => (
                            <Col
                                xxl={2}
                                xl={4}
                                lg={4}
                                md={4}
                                sm={6}
                                key={stat.id || index}
                                className={classNames({
                                    'border-bottom border-lg-0 border-lg-end':
                                        index !== stats.length - 1,
                                    'pb-3 pb-lg-0': index === 0,
                                    'py-3 py-lg-0': index === 1,
                                    'pt-3 pt-lg-0': index === 2
                                })}
                            >
                                <StatsItem stat={stat} />
                            </Col>
                        ))
                    )}
                </Row>
            </Card.Body>
        </Card>
    );
};

StatsItem.propTypes = {
    stat: PropTypes.shape({
        amount: PropTypes.string.isRequired,
        amountComp: PropTypes.string,
        percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        caret: PropTypes.string.isRequired,
        caretColor: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        icon: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    })
};

export default MarketingStats;
